import { Fragment, FunctionComponent, ReactNode, useEffect, useMemo, useState } from 'react';

import { Alert, Box, Stack, Typography } from '@mui/material';
import {
  ArrowRightAlt,
  Autorenew,
  CheckCircleOutline,
  DescriptionOutlined,
  ErrorOutline,
  EventBusy,
  EventRepeat,
  FactCheckOutlined,
  InfoOutlined,
  LockOutlined,
  MailOutline,
  South,
  WarningAmber
} from '@mui/icons-material';

import { Button, Card, FormCheckboxInput, HStack, IModalPropsBase, Modal } from '@dw/components/core';
import { useBinaryToggle, useScreenSize } from '@dw/hooks';
import { DATE_FORMATS } from '@dw/constants';
import {
  IDanceSchoolAnnualTerm,
  ITermDateChangeReport,
  ITermDateChangeReportClassCount,
  TERM_DATE_CHANGE_WARNING_CODE
} from '@dw/models/danceSchool';
import { TColor } from '@dw/models/shared';
import { Dayjs } from '@dw/dayjs';
import { getColorCss, plurify } from '@dw/utils';

interface IAcknowledgeForm {
  acknowledge: boolean;
}

/**
 * Warning codes whose message only restates a count that is already rendered as its own impact item.
 * Rendering both states the same fact twice, so these are dropped from the alert list - unless the
 * warning is what gates the acknowledgement checkbox. Only BoundaryCrossesInvoicedCycle and
 * BoundaryCrossesPaidCycle ever set `requiresAcknowledgement` server-side, so this rule can never hide
 * a warning that drives the gate, and any new code the server adds still surfaces as an alert.
 */
const WARNINGS_SHOWN_AS_ITEMS = new Set<TERM_DATE_CHANGE_WARNING_CODE>([
  TERM_DATE_CHANGE_WARNING_CODE.ATTENDANCE_ROWS_OUTSIDE_WINDOWS,
  TERM_DATE_CHANGE_WARNING_CODE.TRIALS_OUTSIDE_WINDOWS,
  TERM_DATE_CHANGE_WARNING_CODE.EMAILS_ALREADY_SENT_WITH_OLD_DATES
]);

const formatRange = (start: Dayjs, end: Dayjs) =>
  `${start.format(DATE_FORMATS.DayMonthYear)} – ${end.format(DATE_FORMATS.DayMonthYear)}`;

interface IVerdict {
  color: TColor;
  icon: ReactNode;
  headline: string;
  detail: string;
}

/** The single up-front answer to "can I apply these dates?", derived from the report. */
const getVerdict = (report: ITermDateChangeReport): IVerdict => {
  if (report.blocked) {
    const blockerCount = report.blockers.length;
    return {
      color: 'error',
      icon: <ErrorOutline />,
      headline: `These term dates can't be applied`,
      detail: `${blockerCount} ${plurify('problem', blockerCount)} must be fixed first.`
    };
  }

  if (report.changedTerms.length === 0) {
    return {
      color: 'primary',
      icon: <InfoOutlined />,
      headline: 'No changes to apply',
      detail: 'These dates match the current term dates.'
    };
  }

  const termCount = report.changedTerms.length;
  const termSummary = `${termCount} ${plurify('term', termCount)} changing`;

  if (report.requiresAcknowledgement) {
    const ackCount = report.warnings.filter(warning => warning.requiresAcknowledgement).length;
    return {
      color: 'warning',
      icon: <WarningAmber />,
      headline: 'Review before applying',
      detail: `${termSummary}, with ${ackCount} ${plurify('warning', ackCount)} to confirm below.`
    };
  }

  return {
    color: 'success',
    icon: <CheckCircleOutline />,
    headline: 'Ready to apply',
    detail: `${termSummary}.`
  };
};

/** The verdict block that opens the modal - colour coded so the outcome reads at a glance. */
const VerdictBanner: FunctionComponent<IVerdict> = ({ color, icon, headline, detail }) => (
  <HStack
    breakpoint={false}
    columnGap={1.5}
    alignItems='flex-start'
    sx={{
      p: 2,
      borderRadius: 1,
      backgroundColor: getColorCss(color, 0.08),
      borderLeft: `4px solid ${getColorCss(color)}`
    }}
  >
    <Box sx={{ display: 'flex', color: getColorCss(color) }}>{icon}</Box>
    <Stack rowGap={0.25}>
      <Typography fontWeight={600} sx={{ color: getColorCss(color) }}>{headline}</Typography>
      <Typography variant='body2'>{detail}</Typography>
    </Stack>
  </HStack>
);

interface IImpactSectionProps {
  title: string;
  /** When set, the whole section is tinted in that colour so it carries more weight than a plain one. */
  tint?: TColor;
  children: ReactNode;
}
const ImpactSection: FunctionComponent<IImpactSectionProps> = ({ title, tint, children }) => (
  <Stack
    rowGap={1}
    sx={tint ? {
      p: 2,
      borderRadius: 1,
      backgroundColor: getColorCss(tint, 0.06),
      border: `1px solid ${getColorCss(tint, 0.3)}`
    } : undefined}
  >
    <Typography variant='subtitle2' fontWeight={700}>{title}</Typography>
    {children}
  </Stack>
);

interface IImpactItemProps {
  icon: ReactNode;
  color: TColor;
  count: number;
  label: string;
  /** Explanatory clause - kept off the count baseline so every item lines up. */
  caption?: string;
  children?: ReactNode;
}
const ImpactItem: FunctionComponent<IImpactItemProps> = ({ icon, color, count, label, caption, children }) => (
  <Stack rowGap={0.25}>
    <HStack breakpoint={false} columnGap={1}>
      <Box sx={{ display: 'flex', color: getColorCss(color) }}>{icon}</Box>
      <Typography variant='subtitle1' fontWeight={700}>{count}</Typography>
      <Typography variant='body2' fontWeight={600}>{label}</Typography>
    </HStack>
    {(caption || children) && (
      <Stack rowGap={0.5} sx={{ pl: 3.5 }}>
        {caption && <Typography variant='caption'>{caption}</Typography>}
        {children}
      </Stack>
    )}
  </Stack>
);

interface IFeeCycleBreakdownProps {
  classCounts: ITermDateChangeReportClassCount[];
}
/** Per-class detail behind the fee-cycle count. Short lists stay open; longer ones collapse. */
const FeeCycleBreakdown: FunctionComponent<IFeeCycleBreakdownProps> = ({ classCounts }) => {

  const { isOpen, toggle } = useBinaryToggle();

  const alwaysExpanded = classCounts.length <= 3;

  return (
    <Stack rowGap={0.5} alignItems='flex-start'>
      {!alwaysExpanded && (
        <Button variant='text' size='small' onClick={toggle}>
          {isOpen ? 'Hide' : 'Show'} per-class breakdown
        </Button>
      )}
      {(alwaysExpanded || isOpen) && classCounts.map(danceClass => (
        <Typography key={danceClass.danceClassId} variant='caption'>{danceClass.danceClassName}: {danceClass.count}</Typography>
      ))}
    </Stack>
  );
};

interface ITermChangeRow {
  termId: string;
  label: string;
  oldRange: string;
  newRange: string;
}

interface ITermChangesCardProps {
  rows: ITermChangeRow[];
}
/**
 * Before/after per changed term. One CSS grid holds every row so the columns share x-positions and the
 * terms can be compared vertically; on mobile the before/after stack instead of being squeezed.
 */
const TermChangesCard: FunctionComponent<ITermChangesCardProps> = ({ rows }) => {

  const { isMobile } = useScreenSize();

  return (
    <Card sx={{ p: 2, boxShadow: 'none', border: `1px solid ${getColorCss('dark', 0.12)}` }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: isMobile ? 'auto 1fr' : 'auto auto auto 1fr',
          columnGap: 2,
          rowGap: isMobile ? 1.5 : 1,
          alignItems: 'center'
        }}
      >
        {rows.map(row => (
          <Fragment key={row.termId}>
            <Typography variant='body2' fontWeight={600}>{row.label}</Typography>
            {isMobile ? (
              <Stack rowGap={0.25}>
                <Typography variant='body2' color='textDisabled' sx={{ textDecoration: 'line-through' }}>{row.oldRange}</Typography>
                <HStack breakpoint={false} columnGap={0.5}>
                  <South fontSize='small' color='disabled' />
                  <Typography variant='body2' fontWeight={600}>{row.newRange}</Typography>
                </HStack>
              </Stack>
            ) : (
              <>
                <Typography variant='body2' color='textDisabled' sx={{ textDecoration: 'line-through' }}>{row.oldRange}</Typography>
                <ArrowRightAlt fontSize='small' color='disabled' />
                <Typography variant='body2' fontWeight={600}>{row.newRange}</Typography>
              </>
            )}
          </Fragment>
        ))}
      </Box>
    </Card>
  );
};

interface ITermDateChangePreviewModalProps extends IModalPropsBase {
  report?: ITermDateChangeReport;
  /** Stored terms (sorted by start date) used to label each changed term "Term N". */
  terms: IDanceSchoolAnnualTerm[];
  applying: boolean;
  onApply: (acknowledgeWarnings: boolean) => void;
}
export const TermDateChangePreviewModal: FunctionComponent<ITermDateChangePreviewModalProps> = (props: ITermDateChangePreviewModalProps) => {

  const { report, terms, applying, onApply, ...modalBaseProps } = props;

  const [ackForm, setAckForm] = useState<IAcknowledgeForm>({ acknowledge: false });

  useEffect(() => {
    if (modalBaseProps.open) {
      setAckForm({ acknowledge: false });
    }
  }, [modalBaseProps.open]);

  const termNumberById = useMemo(() => {
    const map: Record<string, number> = {};
    terms.forEach((term, index) => { map[term.id] = index + 1; });
    return map;
  }, [terms]);

  if (!report) {
    return null;
  }

  const hasChanges = report.changedTerms.length > 0;
  const applyDisabled = applying || (report.requiresAcknowledgement && !ackForm.acknowledge);

  const termChangeRows: ITermChangeRow[] = report.changedTerms.map(term => ({
    termId: term.termId,
    label: `Term ${termNumberById[term.termId] ?? '?'}`,
    oldRange: formatRange(term.oldStartDate, term.oldEndDate),
    newRange: formatRange(term.newStartDate, term.newEndDate)
  }));

  const hasAutomaticImpact = report.uninvoicedFeeCyclesFollowing > 0
    || report.approvalPendingInvoicesAffected > 0
    || report.enrolmentWindowsShifting > 0
    || report.issuedInvoicesUnchanged > 0;

  const warningAlerts = report.warnings.filter(warning => warning.requiresAcknowledgement || !WARNINGS_SHOWN_AS_ITEMS.has(warning.code));

  const needsAttention = report.attendanceRowsOutsideWindows > 0
    || report.trialsOutsideWindows > 0
    || report.familiesToNotify > 0
    || warningAlerts.length > 0;

  return (
    <Modal<IAcknowledgeForm>
      {...modalBaseProps}
      title='Review term date changes'
      maxWidth='md'
      formWrapper={{
        formData: ackForm,
        formDataChanged: (updated) => setAckForm(updated),
        disabled: applying,
        onSubmit: (fd) => onApply(fd.acknowledge)
      }}
      actions={report.blocked || !hasChanges ? (
        <Button variant='text' onClick={modalBaseProps.onClose}>Close</Button>
      ) : (
        <>
          <Button variant='text' color='error' onClick={modalBaseProps.onClose} disabled={applying}>Cancel</Button>
          <Button variant='text' type='submit' loading={applying} disabled={applyDisabled}>Apply changes</Button>
        </>
      )}
    >
      <Stack rowGap={2}>
        <VerdictBanner {...getVerdict(report)} />

        {report.blocked && (
          <Stack rowGap={1}>
            {report.blockers.map(blocker => (
              <Alert key={blocker.code} severity='error'>{blocker.message}</Alert>
            ))}
          </Stack>
        )}

        {hasChanges && (
          <ImpactSection title='Term changes'>
            <TermChangesCard rows={termChangeRows} />
          </ImpactSection>
        )}

        {/* Nothing below happens while the change is blocked, so none of it is shown. */}
        {!report.blocked && (
          <>
            {hasAutomaticImpact && (
              <ImpactSection title='Happens automatically'>
                {report.uninvoicedFeeCyclesFollowing > 0 && (
                  <ImpactItem
                    icon={<Autorenew fontSize='small' />}
                    color='primary'
                    count={report.uninvoicedFeeCyclesFollowing}
                    label={`un-invoiced ${plurify('fee cycle', report.uninvoicedFeeCyclesFollowing)} regenerate`}
                    caption='They are rebuilt with the new dates.'
                  >
                    {report.uninvoicedFeeCyclesByClass.length > 0 && (
                      <FeeCycleBreakdown classCounts={report.uninvoicedFeeCyclesByClass} />
                    )}
                  </ImpactItem>
                )}
                {report.approvalPendingInvoicesAffected > 0 && (
                  <ImpactItem
                    icon={<DescriptionOutlined fontSize='small' />}
                    color='primary'
                    count={report.approvalPendingInvoicesAffected}
                    label={`draft ${plurify('invoice', report.approvalPendingInvoicesAffected)} updated`}
                    caption='They have not been issued yet, so they can safely change.'
                  />
                )}
                {report.enrolmentWindowsShifting > 0 && (
                  <ImpactItem
                    icon={<EventRepeat fontSize='small' />}
                    color='primary'
                    count={report.enrolmentWindowsShifting}
                    label={`${plurify('enrolment', report.enrolmentWindowsShifting)} get new start and end dates`}
                    caption='Each enrolment window follows the term it belongs to.'
                  />
                )}
                {report.issuedInvoicesUnchanged > 0 && (
                  <ImpactItem
                    icon={<LockOutlined fontSize='small' />}
                    color='success'
                    count={report.issuedInvoicesUnchanged}
                    label={`issued ${plurify('invoice', report.issuedInvoicesUnchanged)} unchanged`}
                    caption='Financial records stay exactly as issued.'
                  />
                )}
              </ImpactSection>
            )}

            {needsAttention && (
              <ImpactSection title='Needs your attention' tint='warning'>
                {report.attendanceRowsOutsideWindows > 0 && (
                  <ImpactItem
                    icon={<FactCheckOutlined fontSize='small' />}
                    color='warning'
                    count={report.attendanceRowsOutsideWindows}
                    label={`attendance ${plurify('record', report.attendanceRowsOutsideWindows)} now sit outside a term`}
                    caption="Nothing is deleted or changed — they're flagged for review."
                  />
                )}
                {report.trialsOutsideWindows > 0 && (
                  <ImpactItem
                    icon={<EventBusy fontSize='small' />}
                    color='warning'
                    count={report.trialsOutsideWindows}
                    label={`${plurify('trial', report.trialsOutsideWindows)} now sit outside a term`}
                    caption="Nothing is deleted or changed — they're flagged for review."
                  />
                )}
                {report.familiesToNotify > 0 && (
                  <ImpactItem
                    icon={<MailOutline fontSize='small' />}
                    color='warning'
                    count={report.familiesToNotify}
                    label={`${report.familiesToNotify === 1 ? 'family' : 'families'} may need re-notifying`}
                    caption='They may still be working off the old dates.'
                  />
                )}
                {warningAlerts.map(warning => (
                  <Alert key={warning.code} severity='warning'>{warning.message}</Alert>
                ))}
              </ImpactSection>
            )}

            {report.requiresAcknowledgement && (
              <Box
                sx={{
                  px: 2,
                  py: 0.5,
                  borderRadius: 1,
                  backgroundColor: getColorCss('warning', 0.08),
                  border: `1px solid ${getColorCss('warning', 0.5)}`
                }}
              >
                <FormCheckboxInput<IAcknowledgeForm> name='acknowledge' label='I understand the above and want to apply these dates.' />
              </Box>
            )}
          </>
        )}
      </Stack>
    </Modal>
  );
};
